/**
 * JourFx Client (Versicherten) Portal
 * Main entry point with Convex + TanStack Router
 */
import i18n from "./i18n";
import React, { Suspense } from "react";
import ReactDOM from "react-dom/client";
import { ConvexReactClient } from "convex/react";
import { ConvexBetterAuthProvider } from "@convex-dev/better-auth/react";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import { authClient } from "./lib/auth";
import { routeTree } from "./routeTree.gen";
import { ErrorBoundary, SkipLink } from "@jourfx/ui";
import "./index.css";
import { applyBrandingFromCache } from "./branding/runtimeBranding";
import { BrandingProvider } from "./branding/BrandingProvider";

// Initialize Convex client
const convex = new ConvexReactClient(
  import.meta.env.VITE_CONVEX_URL || "http://localhost:3210"
);

// Apply cached branding early (before React mount) to reduce flashes.
// Reactive refresh is handled by BrandingProvider inside the component tree.
applyBrandingFromCache(i18n);

// Create router with context
const router = createRouter({
  routeTree,
  context: {
    convex,
  },
  defaultPreload: "intent",
});

// Type declaration for router
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

// Root app component
function App() {
  return (
    <ConvexBetterAuthProvider client={convex} authClient={authClient}>
      <BrandingProvider convex={convex}>
        <RouterProvider router={router} />
      </BrandingProvider>
    </ConvexBetterAuthProvider>
  );
}

// Mount the app - prevent duplicate root creation during HMR
const container = document.getElementById("root")!;
const root = (window as any).__REACT_ROOT__ || ReactDOM.createRoot(container);
(window as any).__REACT_ROOT__ = root;

root.render(
  <React.StrictMode>
    <ErrorBoundary>
      <SkipLink />
      <Suspense fallback={<div className="flex h-screen items-center justify-center">Laden...</div>}>
        <App />
      </Suspense>
    </ErrorBoundary>
  </React.StrictMode>
);
